/**
 * LLD-SPA-062 — "the one global client store." Cross-cutting client state only (§24.2 rule 26);
 * everything else is component state.
 *
 * `accessToken` is the session — LLD-SPA-062's own worked example of what belongs here. It is
 * held in memory ONLY (LLD-SPA-067, SessionPair.yaml: "never in localStorage, never in
 * sessionStorage"); `data/apiClient.ts` reads it via `getAccessToken()` in a request middleware,
 * never by decoding it (LLD-SPA-171 — this store holds the opaque string, nothing parses it).
 *
 * `activeOrgId` / `activeWorkspaceId` (W143) are LLD-SPA-062's other two named examples — "the
 * active workspace" is named explicitly, and the active organisation is the scope one level above
 * it (§27.1). Both are read by many components (the topbar switchers, every workspace-scoped data
 * fetch to come) with no single natural query owner, which is what makes them store slices rather
 * than component state.
 *
 * The address carried between `a-signup`/`a-invite` and `a-confirm` while a sign-up is being
 * confirmed is NOT a store slice — `src/layers/auth/state.ts`'s reducer already carries it on
 * its `a-confirm` state (LLD-SPA-060: a value lives in one state home, never two).
 */
import { useSyncExternalStore } from 'react'

interface AppState {
  readonly accessToken: string | null
  readonly activeOrgId: string | null
  readonly activeWorkspaceId: string | null
}

let state: AppState = { accessToken: null, activeOrgId: null, activeWorkspaceId: null }
const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): AppState {
  return state
}

export function setAccessToken(accessToken: string | null): void {
  state = { ...state, accessToken }
  emit()
}

/** Non-hook read, for `data/apiClient.ts`'s request middleware — middleware is not a component. */
export function getAccessToken(): string | null {
  return state.accessToken
}

/** §27.1 rule 1: "switching organisations is not re-authentication." Switching also drops the
 * active workspace — a workspace id is only ever meaningful within the organisation it belongs
 * to (LLD-SPA-066: leaving a scope drops that scope's cached/selected state). */
export function setActiveOrg(orgId: string | null): void {
  state = { ...state, activeOrgId: orgId, activeWorkspaceId: null }
  emit()
}

export function setActiveWorkspace(workspaceId: string | null): void {
  state = { ...state, activeWorkspaceId: workspaceId }
  emit()
}

/** LLD-SPA-066: signing out drops everything the store holds. */
export function resetOnSignOut(): void {
  state = { accessToken: null, activeOrgId: null, activeWorkspaceId: null }
  emit()
}

export function useAccessToken(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot).accessToken
}

export function useActiveOrgId(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot).activeOrgId
}

export function useActiveWorkspaceId(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot).activeWorkspaceId
}
